import { useEffect, useRef } from 'react';
import {
  useCollection,
  type UseCollectionOptions,
  type UseCollectionResult,
} from '@cloudscape-design/collection-hooks';

/** The slice of a bounded `usePaginatedApi` result that decides when to fetch the next batch. */
interface AutoPagingSource {
  hasMore: boolean;
  fetchingMore: boolean;
  loadMore: () => void;
}

/** `useCollection`'s result, with `openEnd` ready to spread onto `Pagination`. */
type AutoPagedCollection<T> = UseCollectionResult<T> & {
  paginationProps: UseCollectionResult<T>['paginationProps'] & { openEnd: boolean };
};

/**
 * `useCollection` for a table backed by a bounded paginated fetch: paging past the loaded rows
 * fetches the next batch, so the reader never presses a button to continue.
 *
 * It owns all three pieces that have to line up for that to work, so a caller cannot forget one:
 * `allowPageOutOfRange` (without it the index snaps back to page one and the request is swallowed),
 * `openEnd` on the returned `paginationProps` (keeps the next arrow live past the loaded rows), and
 * the effect that spends the held continuation token.
 */
export function useAutoPagedCollection<T>(
  items: ReadonlyArray<T>,
  options: UseCollectionOptions<T>,
  { hasMore, fetchingMore, loadMore }: AutoPagingSource,
): AutoPagedCollection<T> {
  const collection = useCollection(items, {
    ...options,
    pagination: { ...options.pagination, allowPageOutOfRange: true },
  });
  const { currentPageIndex, pagesCount } = collection.paginationProps;
  const { setCurrentPage } = collection.actions;
  const requestedFor = useRef('');

  // A refetch or a filter change reloads from scratch, and usePaginatedApi empties the rows before
  // it does. The reader's position then points at nothing, and keeping it would read as "past the
  // loaded rows" once the first fresh batch lands, so start over from page one.
  const isEmpty = items.length === 0;
  useEffect(() => {
    if (!isEmpty) return;
    requestedFor.current = '';
    setCurrentPage(1);
  }, [isEmpty, setCurrentPage]);

  useEffect(() => {
    if (!hasMore || fetchingMore) return;

    // Driven by paging forward, never by merely arriving. Opening a table then costs exactly the one
    // batch it asked for, even when that batch does not fill a whole page.
    if (currentPageIndex <= 1) return;
    if (currentPageIndex < pagesCount) return;

    // At most one batch per position. A batch can hand back a continuation token and still add no
    // rows, because the service caps how far it reads per request and deduping can drop everything
    // it returned; without this the effect would ask again immediately and poll in a loop.
    const position = `${currentPageIndex}:${pagesCount}`;
    if (requestedFor.current === position) return;

    requestedFor.current = position;
    loadMore();
  }, [hasMore, fetchingMore, currentPageIndex, pagesCount, loadMore]);

  return {
    ...collection,
    // Open-ended while more rows remain: the page count reads as a floor, because the service only
    // says there is more, never how much.
    paginationProps: { ...collection.paginationProps, openEnd: hasMore || fetchingMore },
  };
}
